import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import TriangleRightIcon from "@redotech/redo-icons/play_right_filled.svg";
import TriangleDownIcon from "@redotech/redo-icons/triangle-down_filled.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import {
  FulfillmentGroupData,
  getFulfillmentGroupValue,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import {
  NO_INSURANCE_REASON_MESSAGES,
  PARCEL_THRESHOLD_NOTE_MESSAGES,
  PARCEL_THRESHOLD_REASON,
} from "@redotech/redo-model/outbound-labels/fulfillment-group-insurance";
import { OutboundParcel } from "@redotech/redo-model/outbound-labels/parcel";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  Layout,
  RedoCheckboxCard,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox-card";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { useSlideoutConfig } from "../hooks/use-slideout-config";
import { useInsuranceInfo } from "../utils/use-insurance-info";
import * as additionalFulfillmentCss from "./additional-fulfillment-settings.module.css";
import { ParcelEditSelect } from "./parcels/parcel-edit-select";

const SHIPSURANCE_T_AND_C_URL = "https://shipsurance.com/redo/terms";

export const InsuranceSection = memo(function InsuranceSection({
  fulfillmentGroups,
  parcelBeingEditedPerGroup,
  updateParcel,
  updateRows,
  everythingDisabled,
  isEditingLockedStatus,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
  parcelBeingEditedPerGroup: OutboundParcel[];
  updateParcel: (parcel: Partial<OutboundParcel>) => void;
  updateRows: (updates: FulfillmentGroupUpdate[]) => void;
  everythingDisabled: boolean;
  isEditingLockedStatus: boolean;
}) {
  const { insuranceExpanded: expanded, setInsuranceExpanded: setExpanded } =
    useSlideoutConfig();

  const team = useRequiredContext(TeamContext);

  const isMultiParcel = useMemo(() => {
    return fulfillmentGroups.some(
      (group) =>
        group.selectedParcels?.length && group.selectedParcels.length > 1,
    );
  }, [fulfillmentGroups]);

  const insuranceInfo = useInsuranceInfo({
    parcelBeingEditedPerGroup,
    groups: fulfillmentGroups,
    team,
  });

  const doFullCoverage = useHandler(() => {
    updateRows(
      fulfillmentGroups.map((group) => ({
        fulfillmentGroupId: group._id,
        selectedParcels: group.selectedParcels?.map((parcel) => ({
          ...parcel,
          insurance: { insuredAmount: getFulfillmentGroupValue(group) },
        })),
      })),
    );
  });

  if (!team.settings?.outboundLabels?.insuranceRatePercentage) {
    return null;
  }

  return (
    <Flex dir="column" gap="xs">
      <Flex
        align="center"
        justify="space-between"
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: "pointer" }}
      >
        <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
          Add insurance
        </Text>
        <RedoButton
          IconLeading={expanded ? TriangleDownIcon : TriangleRightIcon}
          size="xs"
        />
      </Flex>

      {expanded && !insuranceInfo.canEdit && (
        <Flex
          align="center"
          dir="column"
          gap="md"
          justify="space-between"
          onClick={() => setExpanded(!expanded)}
          style={{ cursor: "pointer" }}
        >
          <Flex
            bgColor="secondary"
            borderColor="tertiary"
            borderStyle="solid"
            borderWidth="1px"
            p="xl"
            radius="md"
          >
            <Text color="tertiary" fontSize="sm" textAlign="center">
              {NO_INSURANCE_REASON_MESSAGES[insuranceInfo.reason]}
            </Text>
          </Flex>
        </Flex>
      )}

      {expanded && insuranceInfo.canEdit && (
        <Flex dir="column" gap="md" justify="flex-start" pt="md">
          <ParcelEditSelect />
          <Flex dir="column" gap="xxs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {`${fulfillmentGroups.length > 1 ? "Bulk update additional" : "Additional"} shipping insurance`}
              {insuranceInfo.mixedCustomInsurance && " (mixed)"}
            </Text>
            <Text fontSize="xxs" fontWeight="medium" textColor="secondary">
              {`$${insuranceInfo.insuranceIncludedThreshold} included`}
            </Text>
            {insuranceInfo.insuranceCoverageReason !==
              PARCEL_THRESHOLD_REASON.DEFAULT_CARRIER_THRESHOLD && (
              <Text fontSize="xxs" fontWeight="regular" textColor="tertiary">
                {
                  PARCEL_THRESHOLD_NOTE_MESSAGES[
                    insuranceInfo.insuranceCoverageReason
                  ]
                }
              </Text>
            )}
          </Flex>
          <Flex dir="column" gap="lg">
            {insuranceInfo.insuranceIncludedThreshold > 0 && (
              <RedoCheckboxCard
                checked={
                  insuranceInfo.insuranceUiSelection === "defaultCoverage"
                }
                dataTestId="insurance-included-coverage"
                disabled={everythingDisabled || isEditingLockedStatus}
                inputSize="sm"
                inputType="radio"
                layout={Layout.LEFT}
                onClick={() => {
                  updateParcel({ insurance: { insuredAmount: null } });
                }}
                title={
                  <Flex justify="space-between" w="full">
                    <Text fontSize="xs" fontWeight="medium">
                      {`Included coverage up to $${insuranceInfo.insuranceIncludedThreshold}`}
                    </Text>
                    <Text
                      fontSize="xs"
                      fontWeight="regular"
                      textColor="tertiary"
                    >
                      Included
                    </Text>
                  </Flex>
                }
              />
            )}
            {insuranceInfo.totalFullCoverageInsuranceCost > 0 &&
              !isMultiParcel &&
              insuranceInfo.groupCanBeFullyInsured && (
                <RedoCheckboxCard
                  checked={
                    insuranceInfo.insuranceUiSelection === "fullCoverage"
                  }
                  dataTestId="insurance-full-coverage"
                  disabled={everythingDisabled || isEditingLockedStatus}
                  inputSize="sm"
                  inputType="radio"
                  layout={Layout.LEFT}
                  onClick={() => {
                    doFullCoverage();
                  }}
                  title={
                    <Flex justify="space-between" w="full">
                      <Text fontSize="xs" fontWeight="medium">
                        Full coverage
                      </Text>
                      <Text
                        fontSize="xs"
                        fontWeight="regular"
                        textColor="tertiary"
                      >
                        $
                        {insuranceInfo.totalFullCoverageInsuranceCost.toFixed(
                          2,
                        )}
                        {insuranceInfo.isBulkEdit ? " total" : ""}
                      </Text>
                    </Flex>
                  }
                />
              )}
            <RedoCheckboxCard
              checked={insuranceInfo.insuranceUiSelection === "customCoverage"}
              dataTestId="insurance-custom-coverage"
              disabled={everythingDisabled || isEditingLockedStatus}
              inputSize="sm"
              inputType="radio"
              layout={Layout.LEFT}
              onClick={() => {
                updateParcel({
                  insurance: {
                    insuredAmount: insuranceInfo.insuranceIncludedThreshold,
                  },
                });
              }}
              title={
                <Flex justify="space-between" w="full">
                  <Text fontSize="xs" fontWeight="medium">
                    Custom coverage
                  </Text>
                  <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                    {insuranceInfo.insuranceUiSelection !== "customCoverage" ||
                    insuranceInfo.customInsuranceDisplayedCost === 0
                      ? "Included"
                      : `$${insuranceInfo.customInsuranceDisplayedCost.toFixed(2)}${insuranceInfo.isBulkEdit ? " total" : ""}`}
                  </Text>
                </Flex>
              }
            >
              <RedoIncrementDecrement
                disabled={everythingDisabled || isEditingLockedStatus}
                min={insuranceInfo.insuranceIncludedThreshold}
                prefix="$"
                required
                setValue={(newValue) => {
                  updateParcel({ insurance: { insuredAmount: newValue } });
                }}
                size="small"
                step={0.01}
                value={
                  insuranceInfo.insuranceUiSelection === "customCoverage" &&
                  !!insuranceInfo.insuredAmount
                    ? insuranceInfo.insuredAmount
                    : insuranceInfo.insuranceIncludedThreshold
                }
              />
            </RedoCheckboxCard>
          </Flex>
          <Flex>
            <RedoExternalLink
              fontSize="xxs"
              showIcon={false}
              text="Terms and conditions"
              textClassName={additionalFulfillmentCss.linkText}
              url={SHIPSURANCE_T_AND_C_URL}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});
